import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { User, FileText, ArrowLeft, Edit, Mail, Phone, Archive, RefreshCw, AlertCircle, AlertTriangle, Trash2, Copy, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { LoadingPageSkeleton } from "../components/common/LoadingPageSkeleton";
import { Procedure } from "../types";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { ActivityLog } from "../components/ActivityLog";
import api from "../utils/api";
import { toSydneyDate } from "../utils/helpers";

export default function DepartmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { users, procedures, assignDepartmentManager, archiveDepartment, unarchiveDepartment, deleteDepartment } = useData();
  const { currentUser } = useAuth();

  // All state management hooks remain the same
  const [department, setDepartment] = useState<any | null>(null);
  const [departmentUsers, setDepartmentUsers] = useState<any[]>([]);
  const [departmentProcedures, setDepartmentProcedures] = useState<any[]>([]);
  const [openAssignManager, setOpenAssignManager] = useState(false);
  const [openArchive, setOpenArchive] = useState(false);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [selectedManager, setSelectedManager] = useState("");
  const [archiveWarning, setArchiveWarning] = useState<null | { department: any; users: any[] }>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [isAssigning, setIsAssigning] = useState(false);

  // All data fetching and handler logic remains the same
  const fetchDepartment = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get(`/api/departments/${id}`);
      if (data.success) {
        setDepartment(data.department);
        setSelectedManager(data.department.departmentManager);
        setDepartmentProcedures(data.department.procedures);
        setDepartmentUsers(data.department.users);
        let actCount = 0;
        if (data.department.procedures) {
          data.department.procedures.forEach((proc: Procedure) => actCount += (proc.activities?.length || 0));
        }
        setActivitiesCount(actCount);
      }
    } catch (error) { console.error("Error fetching department:", error); }
  }, [id]);

  useEffect(() => {
    fetchDepartment();
  }, [fetchDepartment]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => { if (e.key === 'Enter' && openDelete && deleteConfirmation === 'delete' && department) handleDelete(); };
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [openDelete, deleteConfirmation, department]);

  if (!department) { return <LoadingPageSkeleton />; }

  const eligibleRoles = ["Admin", "Director", "Manager"];
  // Archived users are never selectable as department managers
  const potentialManagers = users.filter(u => !u.archived);
  const managerId = department && typeof department.departmentManager === 'object' && department.departmentManager !== null ? department.departmentManager.id : department?.departmentManager || "";
  const manager = users.find(user => user.id === managerId);
  const canEdit = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director";
  const canArchive = canEdit && !department.archived;
  const canUnarchive = canEdit && department.archived;
  const getUserName = (userId: string) => users.find(u => u.id === userId)?.name || "Unknown";
  const formatArchiveDate = (dateString: string) => toSydneyDate(dateString) || "Unknown";
  const handleDelete = () => { if (deleteConfirmation === 'delete' && department) { deleteDepartment(department.id); navigate('/departments'); } };
  const handleAssignManager = async () => {
    if (department && id) {
      setIsAssigning(true);
      try {
        await assignDepartmentManager(id, selectedManager);
        const selectedUser = users.find(u => u.id === selectedManager);
        if (selectedUser) setDepartment({ ...department, departmentManager: { id: selectedUser.id, name: selectedUser.name, userRole: selectedUser.userRole, jobRole: selectedUser.jobRole, email: selectedUser.email } });
        setOpenAssignManager(false);
        toast.success("Manager assigned successfully");
      } catch (error) {
        toast.error("Failed to assign manager");
      } finally {
        setIsAssigning(false);
      }
    }
  };
  const handleArchive = () => { if (!currentUser || !id) return; archiveDepartment(id, currentUser.id); setOpenArchive(false); setDepartment({ ...department, archived: true, archivedOn: new Date().toISOString(), archivedBy: currentUser.id }); };
  const handleUnarchive = () => { if (!currentUser || !id) return; unarchiveDepartment(id, currentUser.id); setOpenUnarchive(false); setDepartment({ ...department, archived: false, archivedOn: undefined, archivedBy: undefined }); };
  const getDeleteRestrictions = (code: string) => ({ hasUsers: users.some(u => u.department === code), hasProcedures: procedures.some(p => p.departmentId === code) });

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => navigate(-1)}><ArrowLeft size={16} /></Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold leading-tight">{department.departmentCode} - {department.departmentName}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant="outline">Department</Badge>
              {department.archived && <Badge variant="destructive"><Archive size={12} className="mr-1.5" />Archived</Badge>}
            </div>
          </div>
        </div>

        {/* THIS IS THE MODIFIED BUTTON CONTAINER */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full sm:w-auto">
          {!department.archived && (
            <Can I={ACTIONS.UPDATE} a={RESOURCES.DEPARTMENT_MANAGER}>
              <Button variant="outline" onClick={() => {
                setOpenAssignManager(true);
                // Set the select to the current manager's user ID
                let managerId = "";
                if (department && typeof department.departmentManager === 'object' && department.departmentManager !== null && department.departmentManager.id) {
                  managerId = department.departmentManager.id;
                } else if (department && typeof department.departmentManager === 'string') {
                  managerId = department.departmentManager;
                }
                setSelectedManager(managerId || "");
              }
              } className="w-full sm:w-auto justify-center">
                <User className="h-4 w-4 mr-2" />
                {manager ? "Change" : "Assign"} Manager
              </Button>
            </Can>
          )}

          {!department.archived && (
            <Can I={ACTIONS.UPDATE} a={RESOURCES.ARCHIVE_DEPARTMENT}>
              <Button
                variant="outline"
                onClick={() => {
                  // This logic correctly decides which dialog to open
                  if (departmentUsers && departmentUsers.length > 0) {
                    setArchiveWarning({ department, users: departmentUsers });
                  } else {
                    setOpenArchive(true);
                  }
                }}
                className="w-full sm:w-auto justify-center"
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </Button>
            </Can>
          )}

          {department.archived && (
            <Can I={ACTIONS.UPDATE} a={RESOURCES.UNARCHIVE_DEPARTMENT}>
              <Button variant="outline" onClick={() => setOpenUnarchive(true)} className="w-full sm:w-auto justify-center">
                <RefreshCw className="h-4 w-4 mr-2" />
                Unarchive
              </Button>
            </Can>
          )}

          {!department.archived && !department.deleted && (
            <Can I={ACTIONS.DELETE} a={RESOURCES.DEPARTMENT}>
              <Button variant="destructive" onClick={() => setOpenDelete(true)} className="w-full sm:w-auto justify-center">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </Can>
          )}
        </div>
      </div>

      {/* Deleted Banner */}
      {department.deleted && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle size={20} className="text-red-600" />
            <div className="text-red-800">
              <h3 className="font-medium">This department has been deleted</h3>
              <p className="text-sm">Deleted on {formatArchiveDate(department.deletedOn)} by {getUserName(department.deletedBy)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Archive Banner */}
      {department.archived && !department.deleted && (
        <Card className="bg-yellow-50 border-yellow-200"><CardContent className="p-4 flex items-center gap-3"><AlertCircle size={20} className="text-yellow-600" /><div className="text-yellow-800"><h3 className="font-medium">This department is archived</h3><p className="text-sm">Archived on {formatArchiveDate(department.archivedOn)} by {getUserName(department.archivedBy)}</p></div></CardContent></Card>
      )}

      <Tabs defaultValue="details" onValueChange={(value) => {
        if (value === "activity_log") {
          fetchDepartment();
        }
      }}>
        <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
          <TabsTrigger value="details" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Details
          </TabsTrigger>
          <TabsTrigger value="activity_log" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Activity Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6 mt-4">
          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Department Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Department Code</dt>
                    <dd>{department.departmentCode}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Department Name</dt>
                    <dd>{department.departmentName}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Department Manager</CardTitle>
              </CardHeader>
              <CardContent>
                {manager ? (
                  <div className="flex flex-col space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary text-primary-foreground rounded-full p-3">
                        <User size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium">{manager.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">{manager.userRole}</Badge>
                          <p className="text-sm text-muted-foreground">{manager.jobRole}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail size={16} className="text-muted-foreground" />
                        <div className="flex items-center gap-2">
                          {manager.email}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              navigator.clipboard.writeText(manager.email);
                              toast.success("Email copied to clipboard");
                            }}
                          >
                            <Copy size={14} className="text-muted-foreground hover:text-foreground" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone size={16} className="text-muted-foreground" />
                        <span>{manager.mobile}</span>
                      </div>
                    </div>
                    <Link to={`/people/${manager.id}`} className="mt-2">
                      <Button variant="outline" size="sm">View Profile</Button>
                    </Link>
                  </div>
                ) : (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.DEPARTMENT_MANAGER}>
                    <div className="text-center py-6">
                      <User size={48} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No manager assigned</p>
                      {canEdit && !department.archived && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => setOpenAssignManager(true)}
                        >
                          Assign Manager
                        </Button>
                      )}
                    </div>
                  </Can>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Overview Stats */}
          <Card>
            <CardHeader><CardTitle>Overview</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center justify-center p-4 bg-accent rounded-lg">
                  <User size={24} className="mb-2" />
                  <div className="text-2xl">{departmentUsers.length}</div>
                  <div className="text-sm text-muted-foreground">Team Members</div>
                </div>
                <div className="flex flex-col items-center justify-center p-4 bg-accent rounded-lg">
                  <FileText size={24} className="mb-2" />
                  <div className="text-2xl">{departmentProcedures.length}</div>
                  <div className="text-sm text-muted-foreground">Procedures</div>
                </div>
                <div className="flex flex-col items-center justify-center p-4 bg-accent rounded-lg">
                  <ClipboardList size={24} className="mb-2" />
                  <div className="text-2xl">
                    {activitiesCount}
                  </div>
                  <div className="text-sm text-muted-foreground">Activities</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for Users and Procedures */}
          <Tabs defaultValue="users">
            <TabsList className="grid w-full grid-cols-2 sm:w-fit"><TabsTrigger value="users">Team Members</TabsTrigger><TabsTrigger value="procedures">Procedures</TabsTrigger></TabsList>

            <TabsContent value="users" className="mt-4">
              {/* Mobile View: User Cards */}
              <div className="space-y-4 md:hidden">
                {departmentUsers.map(user => (
                  <Card key={user.id}>
                    <CardContent className="p-4">
                      <Link to={`/people/${user.id}`} className="font-bold hover:underline">{user.name}</Link>
                      <p className="text-sm text-muted-foreground">{user.jobRole}</p>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t text-sm">
                        <Badge variant="outline">{user.userRole}</Badge>
                        <span className="text-muted-foreground"
                          onClick={() => {
                            navigator.clipboard.writeText(user.email);
                            toast.success("Email copied to clipboard");
                          }}
                        >
                          {user.email}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {/* Desktop View: User Table */}
              <Card className="hidden md:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Job Role</TableHead>
                        <TableHead>User Role</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {departmentUsers.map(user => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <Link to={`/people/${user.id}`} className="hover:underline font-medium">{user.name}</Link>
                          </TableCell>
                          <TableCell>
                            {user.email}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => {
                                navigator.clipboard.writeText(user.email);
                                toast.success("Email copied to clipboard");
                              }}
                            >
                              <Copy size={14} className="text-muted-foreground hover:text-foreground" />
                            </Button>
                          </TableCell>
                          <TableCell>{user.jobRole}</TableCell>
                          <TableCell>{user.userRole}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              {departmentUsers.length === 0 && <Card><CardContent className="p-6 text-center text-muted-foreground">No team members in this department.</CardContent></Card>}
            </TabsContent>

            <TabsContent value="procedures" className="mt-4">
              {/* Mobile View: Procedure Cards */}
              <div className="space-y-4 md:hidden">
                {departmentProcedures.map(proc => (
                  <Card key={proc.id}>
                    <CardContent className="p-4">
                      <Link to={`/procedure/${proc.id}`} className="font-bold hover:underline">{proc.procedureName}</Link>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t text-sm">
                        <Badge variant="outline">{proc.procedureCadence || "N/A"}</Badge>
                        <span className="text-muted-foreground">Owner: {users.find(u => u.id === (typeof proc.procedureOwner === 'object' ? proc.procedureOwner.id : proc.procedureOwner))?.name || "N/A"}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {/* Desktop View: Procedure Table */}
              <Card className="hidden md:block"><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Cadence</TableHead><TableHead>Owner</TableHead><TableHead>Created On</TableHead></TableRow></TableHeader><TableBody>{departmentProcedures.map(proc => { const owner = users.find(u => u.id === (typeof proc.procedureOwner === 'object' ? proc.procedureOwner.id : proc.procedureOwner)); return (<TableRow key={proc.id}><TableCell><Link to={`/procedure/${proc.id}`} className="hover:underline font-medium">{proc.procedureName}</Link></TableCell><TableCell><Badge variant="outline">{proc.procedureCadence || "N/A"}</Badge></TableCell><TableCell>{owner ? <Link to={`/people/${owner.id}`} className="hover:underline">{owner.name}</Link> : "N/A"}</TableCell><TableCell>{toSydneyDate(proc.createdAt)}</TableCell></TableRow>); })}</TableBody></Table></CardContent></Card>
              {departmentProcedures.length === 0 && <Card><CardContent className="p-6 text-center text-muted-foreground">No procedures in this department.</CardContent></Card>}
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="activity_log" className="space-y-6 mt-4">
          <ActivityLog
            logs={department.activityLogs || []}
            entityName="Department"
            users={users}
            currentUser={currentUser}
          />
        </TabsContent>
      </Tabs>

      {canEdit && !department.archived && (
        <Dialog open={openAssignManager} onOpenChange={(open) => {
          setOpenAssignManager(open);
          if (open) {
            // Set the select to the current manager's user ID
            let managerId = "";
            if (department && typeof department.departmentManager === 'object' && department.departmentManager !== null && department.departmentManager.id) {
              managerId = department.departmentManager.id;
            } else if (department && typeof department.departmentManager === 'string') {
              managerId = department.departmentManager;
            }
            setSelectedManager(managerId || "");
          }
        }}>
          <DialogContent className="w-[95vw] max-w-[600px] sm:w-full">
            <DialogHeader>
              <DialogTitle>Assign Department Manager</DialogTitle>
              <DialogDescription>
                Select a user to assign as the department manager.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground mb-4 break-normal">
                Department managers must have Admin, Director, or Manager roles.
                If you assign a user with a different role, they will be automatically promoted to Manager.
              </p>
              <Select
                value={selectedManager}
                onValueChange={setSelectedManager}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a manager" />
                </SelectTrigger>
                <SelectContent className="w-full min-w-[200px]">
                  {potentialManagers.map((user) => {
                    const isEligible = eligibleRoles.includes(user.userRole);
                    return (
                      <SelectItem
                        key={user.id}
                        value={user.id}
                        className={`${isEligible ? "" : "text-muted-foreground"} break-normal`}
                      >
                        <span className="whitespace-normal break-words">
                          {user.name} - {user.userRole} ({user.jobRole})
                          {!isEligible && " - will be promoted to Manager"}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="sm:flex-row flex-col gap-2">
              <DialogClose asChild>
                <Button variant="outline" className="w-full sm:w-auto">Cancel</Button>
              </DialogClose>
              <Button onClick={handleAssignManager} className="w-full sm:w-auto" disabled={isAssigning}>
                {isAssigning ? "Saving Changes..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}


      {canArchive && (
        <>
          {/* Archive Warning Dialog (active users) */}
          <Dialog open={!!archiveWarning} onOpenChange={(open) => {
            if (!open) setArchiveWarning(null);
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-base font-medium">Archive Department</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Archiving will make this department inactive but preserve its history. Archived departments can be unarchived later.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-[#FEF9C3] border border-[#F7E9A8] rounded-lg p-4">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#854D0E]" />
                    <div className="text-[#854D0E] text-sm">
                      <p className="font-medium mb-2">
                        Warning: This department has active users. You may want to reassign or archive users before archiving the department.
                      </p>
                      <ul className="list-disc list-inside mt-2">
                        {archiveWarning?.users.map((user) => (
                          <li key={user.id}>{user.name} - {user.userRole} ({user.jobRole})</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button onClick={() => {
                  if (!currentUser || !department) return;
                  archiveDepartment(department.id, currentUser.id);
                  setArchiveWarning(null);
                  setDepartment({
                    ...department,
                    archived: true,
                    archivedOn: new Date().toISOString(),
                    archivedBy: currentUser.id
                  });
                }} className="gap-2">
                  <Archive size={16} />
                  Archive Department
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Archive Confirmation Dialog (no active users) */}
          <Dialog open={openArchive} onOpenChange={setOpenArchive}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-base font-medium">Archive Department</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Archiving will make this department inactive but preserve its history. Archived departments can be unarchived later.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm">Are you sure you want to archive the department "{department.departmentName}"?</p>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button onClick={() => {
                  if (!currentUser || !department) return;
                  archiveDepartment(department.id, currentUser.id);
                  setOpenArchive(false);
                  setDepartment({
                    ...department,
                    archived: true,
                    archivedOn: new Date().toISOString(),
                    archivedBy: currentUser.id
                  });
                }} className="gap-2">
                  <Archive size={16} />
                  Archive Department
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {canUnarchive && (
        <Dialog open={openUnarchive} onOpenChange={setOpenUnarchive}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Unarchive Department</DialogTitle>
              <DialogDescription>
                Unarchiving will make this department active again.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p>Are you sure you want to unarchive the department "{department.departmentName}"?</p>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleUnarchive} className="gap-2">
                <RefreshCw size={16} />
                Unarchive Department
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Department</DialogTitle>
          </DialogHeader>
          {getDeleteRestrictions(department.departmentCode).hasUsers || getDeleteRestrictions(department.departmentCode).hasProcedures ? (
            <>
              <div className="py-4">
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Cannot Delete</AlertTitle>
                  <AlertDescription>This department has users or procedures and cannot be deleted. Please archive it instead.</AlertDescription>
                </Alert>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="py-4 space-y-2">
                <p>This action cannot be undone. Type 'delete' to confirm.</p>
                <Input value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button variant="destructive" onClick={handleDelete} disabled={deleteConfirmation !== 'delete'}>Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}